import React, { useState, useEffect } from 'react';
import { messageBoardService } from '../services/messageBoardService';
import { toast } from 'react-toastify';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: '', description: '', event_date: '', event_time: '' });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await messageBoardService.getEvents();
      setEvents(data.events || []);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await messageBoardService.createEvent(newEvent);
      toast.success('Event created!');
      setShowModal(false);
      setNewEvent({ title: '', description: '', event_date: '', event_time: '' });
      fetchEvents();
    } catch (err) {
      toast.error('Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await messageBoardService.deleteEvent(eventId);
      toast.success('Event deleted!');
      setShowEventModal(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  const openEventDetails = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  // Fixed: Use local date formatting to avoid timezone issues
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = formatDateToYYYYMMDD(date);
    return events.filter(e => e.event_date === dateStr);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const formatEventDate = (dateStr) => {
    // Parse the date string as local date (not UTC)
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDisplayDate = (dateStr) => {
    // Parse the date string as local date (not UTC)
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  // Format time from HH:MM:SS or HH:MM to readable format
  const formatEventTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  const days = getDaysInMonth(currentMonth);
  const todayStr = formatDateToYYYYMMDD(new Date());

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Calendar</h1>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Add Event
        </button>
      </div>

      <div className="calendar-container">
        <div className="calendar-header">
          <button onClick={prevMonth}>&lt; Prev</button>
          <h2>{formatMonthYear(currentMonth)}</h2>
          <button onClick={nextMonth}>Next &gt;</button>
        </div>

        <div className="calendar-grid">
          <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="weekday">{day}</div>
            ))}
          </div>

          <div className="calendar-days">
            {days.map((date, index) => {
              const dateStr = date ? formatDateToYYYYMMDD(date) : null;
              const isToday = dateStr === todayStr;
              return (
                <div 
                  key={index} 
                  className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''}`}
                >
                  {date && (
                    <>
                      <span className="day-number">{date.getDate()}</span>
                      <div className="day-events">
                        {getEventsForDate(date).map(event => (
                          <div 
                            key={event.id} 
                            className="event-dot" 
                            title={event.title}
                            onClick={() => openEventDetails(event)}
                            style={{ cursor: 'pointer' }}
                          >
                            {event.title}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Upcoming Events List */}
      <div className="upcoming-events">
        <h3>Upcoming Events</h3>
        {loading ? (
          <p>Loading...</p>
        ) : events.length === 0 ? (
          <p className="no-content">No upcoming events</p>
        ) : (
          <ul className="events-list">
            {events
              .filter(e => e.event_date >= todayStr)
              .sort((a, b) => a.event_date.localeCompare(b.event_date))
              .slice(0, 10)
              .map(event => (
                <li key={event.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ cursor: 'pointer' }} onClick={() => openEventDetails(event)}>
                    <strong>{event.title}</strong>
                    <span style={{ marginLeft: '10px', color: '#A0A0A0' }}>
                      {formatDisplayDate(event.event_date)}
                      {event.event_time && ` at ${formatEventTime(event.event_time)}`}
                    </span>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                    style={{
                      background: '#F87171',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* Add Event Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Add Event</h2>
            <form onSubmit={handleCreateEvent}>
              <div className="form-group">
                <label>Title *</label>
                <input 
                  type="text" 
                  value={newEvent.title}
                  onChange={e => setNewEvent({...newEvent, title: e.target.value})}
                  required 
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  value={newEvent.description}
                  onChange={e => setNewEvent({...newEvent, description: e.target.value})}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input 
                    type="date" 
                    value={newEvent.event_date}
                    onChange={e => setNewEvent({...newEvent, event_date: e.target.value})}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Time</label>
                  <input 
                    type="time" 
                    value={newEvent.event_time}
                    onChange={e => setNewEvent({...newEvent, event_time: e.target.value})}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Event</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="modal-overlay" onClick={() => { setShowEventModal(false); setSelectedEvent(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{selectedEvent.title}</h2>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#A0A0A0', marginBottom: '8px' }}>
                <strong>Date:</strong> {formatEventDate(selectedEvent.event_date)}
              </p>
              {selectedEvent.event_time && (
                <p style={{ color: '#A0A0A0', marginBottom: '8px' }}>
                  <strong>Time:</strong> {formatEventTime(selectedEvent.event_time)}
                </p>
              )}
              {selectedEvent.description && (
                <p style={{ marginTop: '16px' }}>
                  {selectedEvent.description}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={() => handleDeleteEvent(selectedEvent.id)}
                style={{ background: '#F87171', color: 'white' }}
              >
                Delete Event
              </button>
              <button type="button" onClick={() => { setShowEventModal(false); setSelectedEvent(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
